import { memo, useEffect, useRef, useState } from "react";

import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { useSupportedChains } from "@hl/shared-features/lib/features/chain";
import {
  Box,
  Center,
  Group,
  Loader,
  NumberInput,
  OptionalPortal,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { ethers } from "ethers";
import { isEmpty } from "lodash";

import {
  _CollectionType,
  Contract2,
  ContractEntityType,
  DraftProgress,
  NetworkType,
  NftContractStandard,
  PriceType,
  SupportedMediaType,
  TokenDistributionType,
} from "apollo/graphql.generated";
import BlockchainSelect from "components/Select/BlockchainSelect";
import TokenStandardSelect from "components/Select/TokenStandardSelect";
import FormContentWithPreview from "~/features/collections/forms/CreateCollectionMultistepForm/FormContentWithPreview";
import SaveDraftButton from "~/features/collections/forms/CreateCollectionMultistepForm/SaveDraftButton";
import { advanceInputStyles } from "~/features/collections/forms/CreateCollectionMultistepForm/index";
import { getPreviewImageUrl } from "~/features/collections/forms/CreateCollectionMultistepForm/previewUtil";
import CategoriesInput from "~/features/collections/forms/CreateCollectionMultistepForm/tags-categories/CategoriesInput";
import TagsInput, {
  TagInputType,
} from "~/features/collections/forms/CreateCollectionMultistepForm/tags-categories/TagsInput";
import useCurrencyDisplay from "~/features/collections/forms/CreateCollectionMultistepForm/useCurrencyDisplay";
import useSaveDraft from "~/features/collections/forms/CreateCollectionMultistepForm/useSaveDraft";

import CreateContractModal, {
  CreateContractFormValues,
  transform,
} from "./CreateContractModal";
import FormTitle from "./FormTitle";
import NextStepButton from "./NextStepButton";
import SelectContractDropdown from "./SelectContractDropdown";
import TinyTokenMediaUploader from "./TinyBoxedCollectionMediaUploader";
import {
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "./context";

interface CollectionDetailsFormProps {
  title?: string;
  contracts?: Contract2[];
  asQuickDropFlow?: boolean;
}

const DEFAULT_EDITION_SIZE = 100;

type NewContract = {
  type: "new";
  edited: boolean;
  data: {
    name: string;
    symbol: string;
    usingOpenseaBlocklist: boolean;
    collectionLogoImage: string;
    contractStandard: NftContractStandard;
  };
};

const CollectionDetailsForm = ({
  title = "Collection details",
  contracts,
  asQuickDropFlow = false,
}: CollectionDetailsFormProps) => {
  const enable1155 =
    useFeatureFlags(FEATURE_FLAGS.ENABLE_1155_SUPPORT) && asQuickDropFlow;
  const [createContractModalOpen, setCreateContractModalOpen] =
    useState<boolean>(false);

  const [contractId, setContractId] = useState<string | null>(null);
  const [editedCollectionLogo, setEditedCollectionLogo] =
    useState<boolean>(false);

  const { supportedChains, loading: loadingChains } = useSupportedChains();
  const { dispatch } = useCreateCollectionState();
  const form = useCreateCollectionFormContext();
  const formValues = form.values;
  // Set the standard to 1155 as default if quickDrop and 1155 FF enabled
  const hasSetStandard = useRef(false);
  if (!hasSetStandard.current && enable1155) {
    form.values.contract.data.contractStandard = NftContractStandard.ERC1155;
    hasSetStandard.current = true;
  }
  const isAnySeries = [
    _CollectionType.Series,
    _CollectionType.GenerativeSeries,
  ].includes(formValues.collectionType);
  const contractDescription = isAnySeries
    ? "You own and control all contracts created on Highlight. The name of this contract will be publicly visible on blockchain explorers like Etherscan."
    : "Choose which of your contracts to use to deploy this collection. You own and control all contracts created on Highlight. The name of this contract will be publicly visible on blockchain explorers like Etherscan.";
  const { displayCurrency } = useCurrencyDisplay();
  const contractStandard =
    formValues.contract.data.contractStandard ?? NftContractStandard.ERC721;
  const canReuseContracts =
    formValues.collectionType === _CollectionType.LimitedEdition ||
    formValues.collectionType === _CollectionType.OneOfOne ||
    contractStandard === NftContractStandard.ERC1155;
  const contractType = transform(formValues.collectionType, contractStandard);
  let extraContractType: ContractEntityType | null = null;
  if (contractType === ContractEntityType.MultipleEditionsDFS) {
    extraContractType = ContractEntityType.MultipleEditions;
  } else if (contractType === ContractEntityType.SingleEditionDFS) {
    extraContractType = ContractEntityType.SingleEdition;
  }
  const chainId = formValues.collection.chainId;
  const collectionName = formValues.collection.name;

  const symbol = collectionName ? deriveContractSymbol(collectionName) : "";

  const [newContract, setNewContract] = useState<NewContract>(
    formValues.contract.type === "new" && formValues.contract.edited
      ? {
          ...formValues.contract,
          data: {
            ...formValues.contract.data,
            contractStandard: contractStandard,
          },
        }
      : {
          type: "new",
          edited: false,
          data: {
            name: collectionName ?? "",
            symbol,
            contractStandard,
            usingOpenseaBlocklist: false,
            collectionLogoImage:
              form.values.contract.type === "new"
                ? form.values.contract.data.collectionLogoImage ?? ""
                : "",
          },
        }
  );

  const [contractsByChainAndType, setContractsByChainAndType] = useState<
    Contract2[]
  >([]);

  /**
   * Changes the "new contract" object if the collection name changed, and
   * it was not edited by the user.
   */
  useEffect(() => {
    const canClear = !newContract.edited && contractId === "new";
    if (canClear) {
      form.clearFieldError("contract.type");
      form.clearFieldError("contract.data.name");
    }
    setNewContract({
      ...newContract,
      data: {
        ...newContract.data,
        contractStandard,
        name: canClear ? collectionName ?? "" : newContract.data.name,
        symbol: canClear ? symbol : newContract.data.symbol,
      },
    });
  }, [collectionName, symbol, contractStandard]);

  /**
   * Update current contracts filtered by chain and contract type.
   */
  useEffect(() => {
    if (contracts) {
      const contractsByChain =
        contracts?.filter(
          (contract) =>
            contract.chainId.toString() === chainId &&
            (contract.contractType === contractType ||
              (extraContractType &&
                contract.contractType === extraContractType))
        ) || [];
      setContractsByChainAndType(contractsByChain);
    }
  }, [contracts, chainId, contractType]);

  useEffect(() => {
    if (
      contractType === ContractEntityType.EditionsDFS1155 &&
      formValues.distribution.type === TokenDistributionType.Auction
    ) {
      form.setFieldValue("distribution", {
        ...formValues.distribution,
        type: TokenDistributionType.Mint,
        priceType: PriceType.Fixed,
        priceDropInterval: undefined,
        scheduledPrices: undefined,
        price: undefined,
        isFreePrice: false,
      });
    }
  }, [contractType, formValues.distribution]);

  useEffect(() => {
    if (supportedChains && supportedChains.length) {
      form.setFieldValue(
        "collection.chainId",
        formValues.collection.chainId ||
          supportedChains
            .find((chain) => {
              return (
                chain.network === NetworkType.SEPOLIA ||
                chain.network === NetworkType.BASE_SEPOLIA
              );
            })
            ?.chainId.toString() ||
          supportedChains[0].chainId.toString()
      );
    }
  }, [supportedChains, formValues.collection.chainId]);

  /**
   * Sets the contract id when chain has changed (also first render). If a new contract was edited or the users doesn't have contracts,
   * selects this new contract. Otherwise, it selects the first created contract item if it exists.
   */
  useEffect(() => {
    if (
      (formValues.contract.type === "new" && formValues.contract.edited) ||
      !contractsByChainAndType.length ||
      (contractType !== ContractEntityType.MultipleEditionsDFS &&
        contractType !== ContractEntityType.EditionsDFS1155)
    ) {
      setContractId("new");
      form.setFieldValue("contract", {
        ...newContract,
        data: { ...newContract.data, contractStandard },
      });
    } else if (contractsByChainAndType.length) {
      const col = contractsByChainAndType.find((c) => c.id === contractId);
      const cid =
        contractId !== "new" && col ? col.id : contractsByChainAndType[0].id;

      setContractId(cid);
      form.clearFieldError("contract.type");
      form.setFieldValue("contract", {
        type: "existing",
        data: {
          contractId: cid,
          contractStandard,
        },
      });
    }
  }, [
    chainId,
    contractsByChainAndType,
    contractType,
    newContract,
    contractStandard,
    contractId,
  ]);

  const handleCreateContractSubmit = (
    newContractData?: CreateContractFormValues
  ) => {
    const data =
      newContractData || (formValues.contract.data as CreateContractFormValues);
    const newData: NewContract = {
      type: "new",
      edited: true,
      data: {
        name: data.name,
        symbol: data.symbol,
        usingOpenseaBlocklist: false,
        contractStandard: data.contractStandard,
        // cannot set the 'collection logo image - contract image' in the modal
        collectionLogoImage:
          formValues.contract.type === "new"
            ? !isEmpty(formValues.contract.data.collectionLogoImage)
              ? formValues.contract.data.collectionLogoImage
              : formValues.metadata.mediaUrl && !isAnySeries
              ? formValues.metadata.mediaUrl
              : ""
            : "",
      },
    };

    setNewContract(newData);
    setContractId("new");

    form.clearFieldError("contract.type");
    form.setFieldValue("contract", {
      ...newData,
    });
  };

  const { saveDraft, loading } = useSaveDraft({
    furthestStep: DraftProgress.ADDED_DETAILS,
  });

  const onNextStep = () => {
    dispatch({
      type: "NEXT_STEP",
      payload: {
        form,
      },
    });
  };

  const onMediaChanged = (
    media: {
      url: string;
      type: string;
    } | null
  ) => {
    if (media) {
      form.setFieldValue("contract.data.collectionLogoImage", media.url);
      form.setFieldValue("contract.edited", true);
    } else {
      form.setFieldValue("contract.data.collectionLogoImage", "");
    }
    // If user changes chain, we keep the collection logo image
    setNewContract({
      ...newContract,
      data: {
        ...newContract.data,
        contractStandard,
        collectionLogoImage: media?.url || "",
      },
    });
    setEditedCollectionLogo(true);
  };

  const contractOnChange = (contractId: string | null): void => {
    if (!contractId) {
      return;
    }

    if (contractId === "new") {
      form.setFieldValue("contract", {
        ...newContract,
      });
    } else {
      form.setFieldValue("contract", {
        type: "existing",
        data: {
          contractId,
          contractStandard:
            contractsByChainAndType.find((c) => c.id === contractId)
              ?.contractType === ContractEntityType.EditionsDFS1155
              ? NftContractStandard.ERC1155
              : NftContractStandard.ERC721,
        },
      });
    }
    setContractId(contractId);
  };

  const { previewMediaUrl, previewMediaType } = getPreviewImageUrl(form);

  return (
    <Box>
      {!asQuickDropFlow && (
        <FormTitle
          title={title}
          buttons={[
            <SaveDraftButton
              key="draft"
              loading={loading}
              onClick={saveDraft}
            />,
            <NextStepButton
              key="next"
              loading={loading}
              onClick={onNextStep}
            />,
          ]}
        />
      )}
      <FormContentWithPreview
        asQuickDropFlow={asQuickDropFlow}
        collectionType={
          formValues.collectionType ?? _CollectionType.LimitedEdition
        }
        mediaUrl={previewMediaUrl}
        mediaType={previewMediaType}
        showDetails
        name={formValues.collection.name}
        size={formValues.collection.size}
        showPrice={!!form.values.distribution.price}
        price={form.values.distribution.price}
        currency={displayCurrency}
      >
        <Stack spacing={40}>
          {loadingChains ? (
            <Center>
              <Loader />
            </Center>
          ) : (
            <>
              {enable1155 && (
                <TokenStandardSelect
                  value={formValues.contract.data.contractStandard}
                  onChange={(value: NftContractStandard) => {
                    form.setFieldValue("contract.data.contractStandard", value);
                  }}
                />
              )}
              <BlockchainSelect
                searchable={false}
                supportedChains={supportedChains}
                value={formValues.collection.chainId}
                {...form.getInputProps("collection.chainId")}
                onChange={(value: string) => {
                  if (value !== formValues.collection.chainId) {
                    form.setFieldValue(
                      "distribution.currency",
                      ethers.constants.AddressZero
                    );
                  }
                  form.getInputProps("collection.chainId").onChange(value);
                }}
                showNetworkConfirmationModal={
                  formValues.collectionType === _CollectionType.GenerativeSeries
                }
              />
              {canReuseContracts && !!contracts?.length && (
                <Stack spacing={0}>
                  <SelectContractDropdown
                    collectionType={formValues.collectionType}
                    contractId={contractId}
                    newContract={newContract}
                    contractOnChange={contractOnChange}
                    handleCreate={() => setCreateContractModalOpen(true)}
                    contracts={contractsByChainAndType}
                    label="Contract"
                    description={contractDescription}
                    hasError={
                      !!form.errors["contract.type"] ||
                      !!form.errors["contract.data.name"] ||
                      !!form.errors["contract.data.symbol"]
                    }
                  />
                  {form.errors["contract.type"] && (
                    <Text size="xs" color={STATUS_COLOR.ERROR} mt={4}>
                      {form.errors["contract.type"] as string}
                    </Text>
                  )}
                </Stack>
              )}
            </>
          )}
          <TextInput
            placeholder="Name"
            label="Collection name"
            {...form.getInputProps("collection.name")}
          />
          <Textarea
            placeholder="This description will appear publicly in places like marketplaces, third-party apps, and more. Use plaintext or markdown."
            label="Description"
            minRows={3}
            {...form.getInputProps("collection.description")}
            autosize
          />
          {[_CollectionType.LimitedEdition].includes(
            formValues.collectionType
          ) && (
            <NumberInput
              {...form.getInputProps("collection.size")}
              label="Edition Size"
              placeholder={DEFAULT_EDITION_SIZE}
              min={1}
              rightSection={
                <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                  NFTs
                </Text>
              }
              rightSectionWidth={100}
            />
          )}
          <OptionalPortal
            withinPortal={asQuickDropFlow}
            target={"#advanced-options-portal-target"}
            className="optional-portal"
          >
            {(!canReuseContracts || !contracts?.length) && (
              <>
                <TextInput
                  label="Contract name"
                  description="You own and control all contracts created on Highlight. The name of this contract will be publicly visible on blockchain explorers like Etherscan."
                  placeholder="Contract name"
                  onKeyUp={() => handleCreateContractSubmit()}
                  {...form.getInputProps("contract.data.name")}
                />
                <TextInput
                  label="Contract symbol"
                  placeholder="CNTRCT"
                  onKeyUp={() => handleCreateContractSubmit()}
                  {...form.getInputProps("contract.data.symbol")}
                />
              </>
            )}
            <CategoriesInput
              selectedCategories={formValues.collection.categories}
              onChangeCategories={(categories: string[]) =>
                form.setFieldValue("collection.categories", categories)
              }
              collectionType={formValues.collectionType}
              isCurated={formValues.generativeDetails?.curateOutput ?? false}
              chainId={chainId}
            />
            <TagsInput
              userTags={formValues.collection.tags}
              onChangeTags={(tags: TagInputType[]) =>
                form.setFieldValue("collection.tags", tags)
              }
              chainId={chainId}
            />
            {!asQuickDropFlow &&
              [_CollectionType.LimitedEdition].includes(
                formValues.collectionType
              ) && (
                <NumberInput
                  {...form.getInputProps("collection.size")}
                  label="Edition Size"
                  placeholder={DEFAULT_EDITION_SIZE}
                  min={1}
                  rightSection={
                    <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                      NFTs
                    </Text>
                  }
                  rightSectionWidth={100}
                />
              )}
            {isAnySeries && formValues.contract.type === "new" && (
              <Stack spacing={0}>
                <Text component="label" size="sm">
                  Collection logo image
                </Text>
                <Text size="sm" mb={8}>
                  Choose an image to represent this series. This may or may not
                  be the same as one of your token images and will appear on
                  third-party platforms like OpenSea.
                </Text>
                <TinyTokenMediaUploader
                  mediaUrl={formValues.contract.data.collectionLogoImage}
                  mediaType={SupportedMediaType.IMAGE}
                  onMediaChanged={onMediaChanged}
                  mediaError={
                    (form.errors[
                      "contract.data.collectionLogoImage"
                    ] as string) ?? ""
                  }
                  edited={editedCollectionLogo}
                />
              </Stack>
            )}
            <Stack spacing={0}>
              <Text pb="xs" size="sm" component="label">
                Transferability
              </Text>
              <Group
                p={"xs"}
                position="apart"
                align="center"
                sx={advanceInputStyles}
                spacing={0}
                noWrap
              >
                <Text size="sm">Make this collection non-transferable</Text>
                <Switch
                  mt={4}
                  mr={4}
                  sx={{
                    input: {
                      height: "100%",
                    },
                  }}
                  {...form.getInputProps("moduleSettings.nonTransferable", {
                    type: "checkbox",
                  })}
                />
              </Group>
            </Stack>
          </OptionalPortal>
          {!asQuickDropFlow && (
            <NextStepButton size="xl" loading={loading} onClick={onNextStep} />
          )}
        </Stack>
      </FormContentWithPreview>

      <CreateContractModal
        isOpen={createContractModalOpen}
        initialContract={{
          typeData: { type: "contract", val: contractType },
          ...newContract,
        }}
        handleSubmit={handleCreateContractSubmit}
        onClose={() => {
          setCreateContractModalOpen(false);
        }}
      />
    </Box>
  );
};

/**
 * Try to derive contract symbol from collection name.
 * If not possible (name is null or just numbers), use default string.
 * @param name
 */
const deriveContractSymbol = (name?: string | null) =>
  (name ?? "")
    .split("")
    .filter((char) => char.match(/[a-zA-Z]/))
    .join("")
    .toUpperCase()
    .slice(0, 7) || "CNTRCT";

export default memo(CollectionDetailsForm);
